// Zero-cost browser-native speech helpers (Web Speech API).
// No paid keys. speechSynthesis (TTS) is broadly supported; webkitSpeechRecognition
// (STT) works in Chrome/Edge/Android but NOT Safari/iOS — callers must fall back.

export function speechSupport() {
  const tts = typeof window !== "undefined" && "speechSynthesis" in window;
  const SR = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  return { tts, stt: !!SR };
}

let _voices = [];
let _prefs = { name: "", gender: "female", rate: 0.9, pitch: 1 };

export function setVoicePrefs(settings = {}) {
  _prefs = {
    name: settings.default_voice_name || "",
    gender: settings.default_voice_gender || "female",
    rate: settings.voice_rate != null ? Number(settings.voice_rate) : 0.9,
    pitch: settings.voice_pitch != null ? Number(settings.voice_pitch) : 1,
  };
}

export function listBrowserVoices() {
  if (!_voices.length && "speechSynthesis" in window) {
    _voices = window.speechSynthesis.getVoices() || [];
  }
  return _voices;
}

export function loadVoices() {
  return new Promise((resolve) => {
    if (!("speechSynthesis" in window)) return resolve([]);
    const grab = () => {
      _voices = window.speechSynthesis.getVoices() || [];
      if (_voices.length) resolve(_voices);
    };
    grab();
    if (!_voices.length) {
      window.speechSynthesis.onvoiceschanged = () => {
        _voices = window.speechSynthesis.getVoices() || [];
        resolve(_voices);
      };
      // Safety timeout
      setTimeout(() => resolve(_voices), 800);
    }
  });
}

export function pickVoice(lang, gender) {
  if (!_voices.length && "speechSynthesis" in window) {
    _voices = window.speechSynthesis.getVoices() || [];
  }
  // 1. Exact preferred voice name (if it exists on this device).
  if (_prefs.name) {
    const named = _voices.find((v) => v.name === _prefs.name);
    if (named) return named;
  }
  const base = (lang || "en-IN").slice(0, 2).toLowerCase();
  const byLang = _voices.filter((v) => (v.lang || "").toLowerCase().startsWith(base));
  const pool = byLang.length ? byLang : _voices;
  if (!pool.length) return null;
  const wantFemale = (gender || _prefs.gender || "female") === "female";
  const femaleHint = /female|woman|zira|samantha|google.*(uk|us).*female|neerja|swara/i;
  const maleHint = /male|man|david|google.*(uk|us).*male|rishi/i;
  const match = pool.find((v) =>
    (wantFemale ? femaleHint : maleHint).test(v.name || ""));
  return match || pool[0];
}

export function cancelSpeak() {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

// Speak text. opts: { lang, gender, rate, onBoundary(charIndex), onEnd() }
export function speak(text, opts = {}) {
  if (!("speechSynthesis" in window)) return null;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = opts.lang || "en-IN";
  u.rate = opts.rate || _prefs.rate || 0.9;
  u.pitch = opts.pitch != null ? opts.pitch : (_prefs.pitch != null ? _prefs.pitch : 1);
  const v = pickVoice(u.lang, opts.gender);
  if (v) u.voice = v;
  if (opts.onBoundary) u.onboundary = (e) => opts.onBoundary(e.charIndex);
  if (opts.onEnd) u.onend = () => opts.onEnd();
  window.speechSynthesis.speak(u);
  return u;
}

export function createRecognizer(lang) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  r.lang = lang || "en-IN";
  r.continuous = true;
  r.interimResults = true;
  r.maxAlternatives = 1;
  return r;
}

const _norm = (w) => (w || "").toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");

export function tokenize(text) {
  return (text || "").split(/\s+/).map((w) => w).filter(Boolean);
}

// Two-pointer greedy match of spoken transcript against the expected passage.
// Returns { statuses: ('correct'|'wrong')[], correct, total }.
export function matchWords(expectedWords, spokenTranscript) {
  const exp = expectedWords.map(_norm);
  const spoken = tokenize(spokenTranscript).map(_norm);
  const statuses = new Array(exp.length).fill("wrong");
  let sp = 0;
  for (let i = 0; i < exp.length; i++) {
    const target = exp[i];
    if (!target) { statuses[i] = "correct"; continue; }
    // look ahead in the spoken stream within a small window
    let found = -1;
    for (let j = sp; j < Math.min(spoken.length, sp + 4); j++) {
      if (spoken[j] === target) { found = j; break; }
    }
    if (found >= 0) { statuses[i] = "correct"; sp = found + 1; }
  }
  const correct = statuses.filter((s) => s === "correct").length;
  return { statuses, correct, total: exp.length };
}

// Robust continuous recognition session: accumulates ALL final results across
// the whole session (auto-restarting on the browser's silence-triggered onend)
// so long readings are never cut off after a few words. Grade only on stop().
export function createReadingSession(lang, { onTranscript } = {}) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  let rec = null, finalT = "", lastFull = "", userStopped = false;
  const handle = (e) => {
    let interim = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) finalT += t + " "; else interim += t + " ";
    }
    lastFull = (finalT + interim).replace(/\s+/g, " ").trim();
    if (onTranscript) onTranscript(lastFull);
  };
  const make = () => {
    rec = new SR();
    rec.lang = lang || "en-IN";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = handle;
    rec.onerror = () => {};   // ignore transient no-speech/aborted; onend handles restart
    rec.onend = () => {
      if (userStopped) return;
      try { make(); rec.start(); } catch { /* will settle on next tick */ }
    };
  };
  return {
    start() { userStopped = false; finalT = ""; lastFull = ""; make(); rec.start(); },
    stop() { userStopped = true; try { rec && rec.stop(); } catch { /* */ } return lastFull; },
    getTranscript() { return lastFull; },
  };
}

// Zero-cost microphone capture via MediaRecorder. Returns { start, stop } where
// stop() resolves to an audio Blob (audio/webm when supported).
export async function createAudioRecorder() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia ||
      typeof MediaRecorder === "undefined") return null;
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
    : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
  const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  return {
    start() { try { mr.start(); } catch { /* */ } },
    stop() {
      return new Promise((resolve) => {
        mr.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          resolve(new Blob(chunks, { type: mime || "audio/webm" }));
        };
        try { mr.stop(); } catch { resolve(new Blob(chunks, { type: mime || "audio/webm" })); }
      });
    },
  };
}

